import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { functions } from '../../firebase';
import { Mail, Settings } from 'lucide-react';
import Payments from '../Payments';

function Profile() {
  const { currentUser, currentOrg, subscription, trialInfo, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [userData, setUserData] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [showPayments, setShowPayments] = useState(false);
  const [marketingPreference, setMarketingPreference] = useState(false);
  const [marketingLoading, setMarketingLoading] = useState(false);
  const t = useTranslation();

  useEffect(() => {
    async function fetchUserData() {
      if (currentUser?.uid) {
        try {
          const userDoc = await getDoc(doc(db, "users", currentUser.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserData(data);
            setMarketingPreference(data.marketing || false);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      }
    }

    fetchUserData();
  }, [currentUser]);

  async function handleLogout() {
    setError('');
    
    try {
      setLoading(true);
      await logout();
      // Logout successful - the AuthContext will update the currentUser
    } catch (error) {
      setError(t('errors.general'));
      console.error("Logout error:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleMarketingPreferenceChange(enabled) {
    if (!currentUser) {
      setError(t('errors.general'));
      return;
    }

    try {
      setMarketingLoading(true);
      setError('');

      // Update the marketing preference in Firestore
      await updateDoc(doc(db, "users", currentUser.uid), {
        marketing: enabled
      });

      // Update local state
      setMarketingPreference(enabled);
      setMessage(enabled 
        ? t('profile.marketingEnabled', 'Marketing notifications enabled') 
        : t('profile.marketingDisabled', 'Marketing notifications disabled')
      );

      // Clear the message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating marketing preference:', error);
      setError(t('errors.general'));
    } finally {
      setMarketingLoading(false);
    }
  }

  async function handleManageSubscription() {
    // Ensure currentUser is available before proceeding
    if (!currentUser) {
      setError(t('errors.general'));
      return; 
    }

    try {
      setPortalLoading(true);
      setError('');

      // Refresh the token
      await currentUser.getIdToken(true);
      
      if (!currentOrg?.stripeCustomerId || !subscription) {
        // If the organization doesn't have a subscription, show the payments modal
        setShowPayments(true);
        setPortalLoading(false);
        return;
      }
      
      // Direct fetch to the createPortalSessionSimple function
      const token = await currentUser.getIdToken();
      
      // Cloud function URL
      const region = 'us-central1'; // or your specific region
      const projectId = 'sanctions-screener-20d59'; // your Firebase project ID
      const functionUrl = `https://${region}-${projectId}.cloudfunctions.net/createPortalSessionSimple`;
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          returnUrl: window.location.origin + '/profile'
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Open the portal URL in a new tab
      if (data.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (error) {
      console.error('Error creating portal session:', error);
      setError(t('errors.serverError'));
    } finally {
      setPortalLoading(false);
    }
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {showPayments && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-4xl">
            <Payments isUpgrade={true} onClose={() => setShowPayments(false)} />
          </div>
        </div>
      )}
      
      <div className="max-w-md w-full mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">{t('profile.title')}</h2>
          {currentUser?.displayName && (
            <p className="text-gray-600 dark:text-gray-400">{t('common.welcome')}, {currentUser.displayName}</p>
          )}
        </div>
        
        {error && <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-md">{error}</div>}
        {message && <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 rounded-md">{message}</div>}
        
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('auth.email')}
            </label>
            <input
              type="email"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
              value={currentUser?.email || ''}
              readOnly
            />
          </div>
          
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('auth.name')}
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
              value={currentUser?.displayName || ''}
              readOnly
            />
          </div>

          {/* Organization Information */}
          {currentOrg && (
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('profile.organization', 'Organization')}
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                value={currentOrg.name || ''}
                readOnly
              />
            </div>
          )}

          {/* User Role */}
          {userData?.role && (
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('profile.role', 'Role')}
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                value={userData.role === 'admin' ? t('profile.admin', 'Organization Admin') : t('profile.member', 'Member')}
                readOnly
              />
            </div>
          )}
          
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('profile.accountCreated', 'Account Created')}
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
              value={currentUser?.metadata?.creationTime ? new Date(currentUser.metadata.creationTime).toLocaleString() : ''}
              readOnly
            />
          </div>
          
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('profile.lastSignIn', 'Last Sign In')}
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
              value={currentUser?.metadata?.lastSignInTime ? new Date(currentUser.metadata.lastSignInTime).toLocaleString() : ''}
              readOnly
            />
          </div>
          
          {/* Organization Subscription Management Section */}
          <div className="mt-6 border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">{t('profile.organizationSubscription', 'Organization Subscription')}</h3>
            
            {/* Trial Mode Information */}
            {trialInfo?.isInTrial && (
              <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                <div className="flex items-start">
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      {t('trial.activeTitle', 'Free Trial Active')}
                    </h4>
                    <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                      <p className="mb-2">
                        {t('trial.description', 'You\'re currently enjoying full access to all Professional features during your free trial period.')}
                      </p>
                      {trialInfo.daysRemaining && (
                        <p className="font-medium">
                          {t('trial.daysRemaining', `${trialInfo.daysRemaining} days remaining in your trial.`)}
                        </p>
                      )}
                      <p className="mt-2">
                        {t('trial.upgradePrompt', 'Upgrade to a paid plan before your trial ends to continue using all features.')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {subscription ? (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{t('profile.status', 'Status')}</span>
                  <span className={`text-sm font-medium ${trialInfo?.isInTrial ? 'text-yellow-600 dark:text-yellow-400' : (subscription.status === 'active' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}`}>
                    {trialInfo?.isInTrial 
                      ? t('trial.status') 
                      : (subscription.status === 'active' 
                          ? t('profile.active', 'Active') 
                          : (subscription.status === 'past_due' 
                              ? t('profile.paymentOverdue', 'Payment Overdue')
                              : t(`profile.${subscription.status}`, subscription.status)))}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{t('profile.tier', 'Tier')}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {subscription.productName || (trialInfo?.isInTrial ? t('profile.professional', 'Professional') : t('profile.starter', 'Starter'))}
                  </span>
                </div>
                {subscription.currentPeriodEnd && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{t('profile.nextBilling', 'Next Billing')}</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {new Date(subscription.currentPeriodEnd.seconds * 1000).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                {t('profile.noOrganizationSubscription', 'Your organization doesn\'t have an active subscription.')}
              </p>
            )}
            {/* Only show subscription management button for organization admins */}
            {userData?.role === 'admin' && (
              <button
                className="w-full mt-3 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleManageSubscription}
                disabled={portalLoading}
              >
                {portalLoading ? t('common.loading') : subscription ? t('profile.manageSubscription', 'Manage Subscription') : t('profile.subscribeNow', 'Subscribe Now')}
              </button>
            )}
            {userData?.role !== 'admin' && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                {t('profile.contactAdmin', 'Contact your organization admin to manage billing and subscriptions.')}
              </p>
            )}
          </div>

          {/* Marketing Preferences Section */}
          <div className="mt-6 border-t pt-6">
            <div className="flex items-center mb-3">
              <Mail className="w-5 h-5 text-gray-500 dark:text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('profile.marketingPreferences', 'Marketing Preferences')}</h3>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 mr-4">
                  <div className="flex items-center mb-2">
                    <Settings className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2" />
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      {t('profile.sanctionsUpdates', 'Sanctions & Compliance Updates')}
                    </h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    {t('profile.marketingDescription', 'Receive priority alerts about critical sanctions updates, regulatory changes, and new compliance features that could impact your business.')}
                  </p>
                </div>
                
                <div className="flex-shrink-0">
                  <button
                    onClick={() => handleMarketingPreferenceChange(!marketingPreference)}
                    disabled={marketingLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                      marketingPreference 
                        ? 'bg-blue-600 dark:bg-blue-500' 
                        : 'bg-gray-200 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        marketingPreference ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
              
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {marketingPreference 
                    ? t('profile.marketingOn', 'You will receive marketing communications') 
                    : t('profile.marketingOff', 'You will not receive marketing communications')
                  }
                </span>
                {marketingLoading && (
                  <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
                    {t('common.saving', 'Saving...')}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <button
            className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed mt-4"
            onClick={handleLogout}
            disabled={loading}
          >
            {loading ? t('common.processing') : t('auth.logout')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Profile;